if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        if (this.classList.contains('media-gallery--thumbs-left')) this.initThumbnailArrows();
      }

      // Thumbnails left of the image: the chosen one and its neighbours are bigger (data-distancia 0/1/2, CSS).
      markDistances(thumbnail) {
        const items = Array.from(thumbnail.parentElement.children).filter((item) => item.offsetParent !== null);
        const index = items.indexOf(thumbnail);
        if (index < 0) return;
        items.forEach((item, position) => (item.dataset.distancia = Math.min(Math.abs(position - index), 2)));
      }

      // Thumbnails left of the image: a column from 990px, a row below that. SliderComponent only
      // scrolls sideways, so these arrows scroll the list along its current axis.
      initThumbnailArrows() {
        const list = this.elements.thumbnails.slider;
        const previous = this.elements.thumbnails.querySelector('button[name="thumbnails-previous"]');
        const next = this.elements.thumbnails.querySelector('button[name="thumbnails-next"]');
        if (!list || !previous || !next) return;

        const update = () => {
          const { position, size, length } = this.thumbnailsAxis();
          this.elements.thumbnails.classList.toggle('thumbnail-slider--overflow', length - size > 1);
          previous.disabled = position < 1;
          next.disabled = position + size > length - 1;
        };
        const scroll = (event) => {
          const items = Array.from(list.children).filter((item) => item.clientHeight > 0);
          if (items.length < 2) return;
          const { vertical } = this.thumbnailsAxis();
          const offset = vertical ? items[1].offsetTop - items[0].offsetTop : items[1].offsetLeft - items[0].offsetLeft;
          const distance = offset * (event.currentTarget.dataset.step || 1) * (event.currentTarget === next ? 1 : -1);
          list.scrollBy(vertical ? { top: distance } : { left: distance });
        };

        previous.addEventListener('click', scroll);
        next.addEventListener('click', scroll);
        list.addEventListener('scroll', update, { passive: true });
        new ResizeObserver(update).observe(list);

        // With a mouse, hovering a thumbnail shows its photo, like a click but without scrolling the page. The first
        // time the pointer enters the column, the other photos start loading, so they are ready when hovered.
        if (!window.matchMedia('(hover: hover)').matches) return;
        list.addEventListener(
          'pointerenter',
          () => this.elements.viewer.querySelectorAll('img[loading="lazy"]').forEach((image) => (image.loading = 'eager')),
          { once: true }
        );
        list.querySelectorAll('[data-target]').forEach((item) =>
          item.addEventListener('mouseenter', () => {
            if (!item.querySelector('[aria-current]')) this.setActiveMedia(item.dataset.target, false, true);
          })
        );
      }

      thumbnailsAxis() {
        const list = this.elements.thumbnails.slider;
        const vertical = getComputedStyle(list).flexDirection === 'column';
        return vertical
          ? { vertical, position: list.scrollTop, size: list.clientHeight, length: list.scrollHeight }
          : { vertical, position: list.scrollLeft, size: list.clientWidth, length: list.scrollWidth };
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      // fromHover: true only from the thumbnail hover (a click passes its event here).
      setActiveMedia(mediaId, prepend, fromHover) {
        const hover = fromHover === true;
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        if (!hover) {
          this.preventStickyHeader();
          window.setTimeout(() => {
            if (!this.mql.matches || this.elements.thumbnails) {
              activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
            }
            const activeMediaRect = activeMedia.getBoundingClientRect();
            // Don't scroll if the image is already in view
            if (activeMediaRect.top > -0.5) return;
            const top = activeMediaRect.top + window.scrollY;
            window.scrollTo({ top: top, behavior: 'smooth' });
          });
        }
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (!hover) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        this.markDistances(thumbnail);
        if (this.thumbnailsAxis().vertical) {
          // The column stays put; it only scrolls (smoothly, CSS) when the chosen thumbnail is out of view.
          const list = this.elements.thumbnails.slider;
          const { offsetTop: top, offsetHeight: height } = thumbnail;
          if (top < list.scrollTop) list.scrollTo({ top: top - 8 });
          else if (top + height > list.scrollTop + list.clientHeight) list.scrollTo({ top: top + height - list.clientHeight + 8 });
          return;
        }
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
